import { Component, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonService } from '@app/core/services/common.service';
import { ProfileService } from '@app/core/services/profile.service';
import { StripePaymentService } from '@app/modules/account/service/stripe.service';
import { eUserType } from '@app/shared/enum/user-type';
import { StripeCardElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { StripeCardComponent, StripeFactoryService, StripeInstance, StripeService } from 'ngx-stripe';
import { getFriendlyStripeErrorMessage } from '@app/modules/account/utils/stripe-error-message.util';
import { subscriptionRenewalMessages } from '@app/configs/app-message.config';
import { generateIdempotencyKey } from '@app/shared/idempotency-key';
import { SUBSCRIPTION_DATE_FORMAT, toSubscriptionInfo } from '@app/shared/models/subscription-info.model';
import { getSubscriptionProfileEndpoint } from '@app/shared/subscription-routes';
import {
  calculateNextChargeDate,
  describePlan,
  RenewSubscriptionRequest,
  SavedCard,
  SubscriptionPlan
} from '@app/shared/models/subscription-renewal.model';

// Bad request, payment declined, plan/card not found: nothing was charged or extended.
const DEFINITIVE_REJECTIONS = [400, 402, 404];

@Component({
  selector: 'app-update-subscription-payment',
  templateUrl: './update-subscription-payment.component.html',
  styleUrls: ['./update-subscription-payment.component.scss']
})
export class UpdateSubscriptionPaymentComponent implements OnInit {


  //#region Subscription Members
  _accessToken: string;
  _planId: string;
  _userType: number;


  _processingPayment = false;
  _paymentSuccessfull = false;
  isCardValid: any;

  // One key per renewal attempt: kept while a result is unknown, replaced after a definitive failure.
  _idempotencyKey = generateIdempotencyKey();
  _plans: SubscriptionPlan[] = [];
  _savedCard: SavedCard | null = null;
  /** The owner's current expiry, read from the profile; null until it is known or when it cannot be read. */
  currentExpiry: string | null = null;
  readonly dateFormat = SUBSCRIPTION_DATE_FORMAT;
  _payWithSavedCard = false;
  _statusMessage = '';
  _errorMessage = '';

  @ViewChild(StripeCardComponent) card: StripeCardComponent;

  cardOptions: StripeCardElementOptions = {
    style: {
      base: {
        iconColor: "#666EE8",
        color: "#31325F",
        fontWeight: "300",
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: "18px",
        "::placeholder": {
          color: "#CFD7E0",
        },
      },
    },
  };

  elementsOptions: StripeElementsOptions = {};

  stripeTest: FormGroup;
  stripe: StripeInstance;
  token: string;

  //#endregion

  //#region Life cycle hooks
  constructor(
    private _renderer: Renderer2,
    private fb: FormBuilder,
    private stripeService: StripeService,
    private _router: Router,
    private _stripePaymentService: StripePaymentService,
    private _commonService: CommonService,
    private stripeFactory: StripeFactoryService,
    private profileService: ProfileService
  ) { }
  
  
  get eUserType() {
    return eUserType;
  }

  ngOnInit(): void {
    this._accessToken = this._stripePaymentService.accessToken;
    this._planId = this._stripePaymentService.planId;
    this._userType = parseInt(localStorage.getItem('userType'));
    this._renderer.addClass(document.body, "bg-body");
    this.stripe = this.stripeFactory.create(
      "pk_test_51HS27CGULtR73YOeci3Ls3Dw99rONWrGfoI29hgacBtOQpBoOjbmRMvDviOlwdChgblN0ACSe27GqzIF9yfarNcL00s4JJvTFW"
    );
    this.stripeTest = this.fb.group({
      name: ["", [Validators.required]],
    });
    this.cardOptions.hidePostalCode = true;
    this.noToken()
    if (this._accessToken && this._planId) {
      this.loadPlans();
      this.loadSavedCard();
      this.loadCurrentExpiry();
    }
  }

  get selectedPlan(): SubscriptionPlan | undefined {
    return this._plans.find(plan => plan.id === this._planId);
  }

  get currentPlan(): SubscriptionPlan | undefined {
    return this._plans.find(plan => plan.id === this._stripePaymentService.currentPlan);
  }

  get selectedPlanInfo() {
    return describePlan(this.selectedPlan);
  }

  get currentPlanInfo() {
    return describePlan(this.currentPlan);
  }

  /**
   * The day the next charge falls on once this renewal is paid, as the start of that UTC day; null (the line is left out)
   * when it cannot be worked out. A whole day, not a moment, so the value stays the same between change detection passes.
   */
  get nextChargeDate(): string | null {
    const plan = this.selectedPlan;
    const expiry = this.currentExpiry ? new Date(this.currentExpiry) : null;
    if (!plan || !expiry || isNaN(expiry.getTime())) {
      return null;
    }
    const next = calculateNextChargeDate(expiry, this.now(), plan.interval);
    return `${next.toISOString().slice(0, 10)}T00:00:00.000Z`;
  }

  /** The current time; a method so a test can fix it. */
  now(): Date {
    return new Date();
  }

  get canPay(): boolean {
    return this._payWithSavedCard || !!this.isCardValid;
  }

  private loadPlans(): void {
    this._stripePaymentService.subscriptionPlans(this._accessToken).subscribe(
      (res: any) => {
        if (res && (res.statusCode == 200 || res.statusCode == 201)) {
          this._plans = res.data || [];
        }
      },
      () => { /* the summary simply omits plan details */ }
    );
  }

  private loadCurrentExpiry(): void {
    const endpoint = getSubscriptionProfileEndpoint(this._userType);
    if (!endpoint) {
      return;
    }
    this.profileService.getProfile(endpoint).subscribe(
      (res: any) => {
        if (res && Number(res.statusCode) === 200) {
          const info = toSubscriptionInfo(res.data);
          this.currentExpiry = info && info.expiryDate ? info.expiryDate : null;
        }
      },
      () => { /* the summary simply omits the next charge date */ }
    );
  }

  private loadSavedCard(): void {
    this._stripePaymentService.getSavedCard(this._accessToken).subscribe(
      (res: any) => {
        if (res && res.statusCode == 200 && res.data) {
          this._savedCard = res.data;
          this._payWithSavedCard = true;
        }
      },
      () => { /* no saved card: only the new card option is shown */ }
    );
  }


  noToken() {
    if (!this._accessToken || !this._planId) {
      this.redirectToProfile()
    }
  }

  ngOnDestroy() {
    this._renderer.removeClass(document.body, "bg-body");
  }

  onCardChange(event){
    this.isCardValid = event.complete
  }
  //#endregion


  //#region Helper functions

  onPaymentMethodChange(useSavedCard: boolean): void {
    if (!this._processingPayment) {
      this._payWithSavedCard = useSavedCard && !!this._savedCard;
    }
  }

  /** Single entry point for the Pay button; ignores calls while a renewal is in flight. */
  onPay(): void {
    if (this._processingPayment || this._paymentSuccessfull || !this.canPay) {
      return;
    }
    this._processingPayment = true;
    this._errorMessage = '';
    this._statusMessage = subscriptionRenewalMessages.processing;
    if (this._payWithSavedCard) {
      this.submitRenewal();
    } else {
      this.createToken();
    }
  }

  createToken(): void {
    this._commonService.showPageLoader("UpdateSubscriptionPaymentComponent-createToken");
    const name = this.stripeTest.get("name").value;
    this.stripeService
      .createToken(this.card.element, { name })
      .subscribe((result) => {
        if (result && result.token) {
          // Use the token
          this.token = result.token.id;
          this._commonService.hidePageLoader("UpdateSubscriptionPaymentComponent-createToken");
          this.submitRenewal();
        } else if (result && result.error) {
          // Error creating the token
          this.token = "";
          this._commonService.hidePageLoader("UpdateSubscriptionPaymentComponent-createToken");
          this.showTokenError(result.error);
        }
      }, (error) => {
        this._commonService.hidePageLoader("UpdateSubscriptionPaymentComponent-createToken");
        this.showTokenError(error);
      });
  }

  private showTokenError(error: any): void {
    this._processingPayment = false;
    this._statusMessage = '';
    this._errorMessage = getFriendlyStripeErrorMessage(error);
    this._commonService.showSnakeBar(this._errorMessage);
  }

  submitRenewal(): void {
    const request: RenewSubscriptionRequest = {
      planId: this._planId,
      idempotencyKey: this._idempotencyKey
    };
    if (this._payWithSavedCard) {
      request.useSavedCard = true;
    } else {
      request.tokenId = this.token;
    }

    this._commonService.showPageLoader("UpdateSubscriptionPaymentComponent-onSubmit");
    this._stripePaymentService
      .renewSubscription(request, this._accessToken)
      .subscribe(
        (res) => {
          this._commonService.hidePageLoader("UpdateSubscriptionPaymentComponent-onSubmit");
          if (res.statusCode == 200 || res.statusCode == 201) {
            this.onRenewalSucceeded();
          } else if (DEFINITIVE_REJECTIONS.includes(Number(res.statusCode))) {
            this.onRenewalRejected(res.message);
          } else {
            this.onRenewalUnknown(res.message);
          }
        },
        (error) => {
          this._commonService.hidePageLoader("UpdateSubscriptionPaymentComponent-onSubmit");
          const status = error && error.status;
          if (status >= 400 && status < 500) {
            this.onRenewalRejected(getFriendlyStripeErrorMessage(error));
          } else {
            this.onRenewalUnknown();
          }
        }
      );
  }

  private onRenewalSucceeded(): void {
    this._paymentSuccessfull = true;
    this._processingPayment = false;
    this._errorMessage = '';
    this._statusMessage = subscriptionRenewalMessages.success;
    this._stripePaymentService.currentPlan = this._planId;
    this._commonService.showSnakeBar(subscriptionRenewalMessages.success);
    setTimeout(() => this.redirectToProfile(), 1000);
  }

  /** The server decided not to renew: nothing was charged or extended, so the retry needs a new key. */
  private onRenewalRejected(message?: string): void {
    this._processingPayment = false;
    this._statusMessage = '';
    this._errorMessage = message || subscriptionRenewalMessages.failure;
    this._idempotencyKey = generateIdempotencyKey();
    this._commonService.showSnakeBar(this._errorMessage);
  }

  /** No answer from the server: the renewal may have succeeded, so a retry must reuse the same key. */
  private onRenewalUnknown(message?: string): void {
    this._processingPayment = false;
    this._statusMessage = '';
    this._errorMessage = message
      ? `${message} ${subscriptionRenewalMessages.unknown}`
      : subscriptionRenewalMessages.unknown;
    this._commonService.showSnakeBar(this._errorMessage);
  }

  redirectToProfile() {
    if (this._userType == eUserType.SHIPPER) {
      this._router.navigate(['shipper/profile'])
    }
     else if (this._userType == eUserType.FINANCIAL_ADMIN) {
      this._router.navigate(['shipper/user-profile'])

    }
    else if (this._userType == eUserType.CARRIER) {
      this._router.navigate(['carrier/profile'])
      
    }
       else if (this._userType == eUserType.CARRIER_FINANCIAL_ADMIN) {
      this._router.navigate(['carrier/user-profile'])
      
    }
    else if (this._userType == eUserType.OWNER_OPERATOR) {
      this._router.navigate(['owner-operator/profile'])
      
    }
  }

}

export class PaymentModel {
  planId: string = undefined;
  tokenId: string = undefined;
}
